// src/pages/SpendingAnalysis.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
    Box, Typography, MenuItem, Select,
    Dialog, Divider, IconButton, useMediaQuery,
    LinearProgress, Button, Container,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
    TrendingDownOutlined, FileDownloadOutlined, InfoOutlined,
    CalendarTodayOutlined, WarningAmberOutlined, CheckCircleOutline,
    CloseOutlined,
} from "@mui/icons-material";
import {
    PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";

/* ─── Brand ──────────────────────────────────────────────────────── */
const B = {
    deep:    '#0D0820',
    dark:    '#1A0F3D',
    mid:     '#2D1B69',
    purple:  '#7B5EA7',
    light:   '#9B7EC8',
    accent:  '#C8B8E8',
    lavender:'#F7F4FC',
    ink:     '#0D0A1A',
    green:   '#00C896',
    red:     '#FF4D6A',
    redDim:  '#FF4D6A18',
    orange:  '#FF9500',
};

const spendingData = [
    { name: "Food",               value: 30000, color: B.orange,  emoji: "🍽️", tip: "Consider meal prepping to reduce dining-out costs.",                    transactions: [{ date: "Aug 4",  description: "Groceries",          amount: 18000, implication: "No direct impact, but budgeting matters for financial health." }, { date: "Aug 12", description: "Dining Out",        amount: 12000, implication: "Eating out can add up quickly." }] },
    { name: "Credit Utilization", value: 20000, color: B.red,     emoji: "💳", tip: "Keep credit utilization below 30% to protect your score.",              transactions: [{ date: "Aug 7",  description: "Credit Card Spend",  amount: 20000, implication: "High utilization can harm your credit score significantly." }] },
    { name: "Bills",              value: 25000, color: B.purple,  emoji: "⚡", tip: "Set up direct debits to avoid late payment penalties.",                 transactions: [{ date: "Aug 2",  description: "Electricity",        amount: 15000, implication: "Timely bill payments help boost your credit score." }, { date: "Aug 15", description: "Water Bill",         amount: 10000, implication: "Late payments can negatively impact your credit score." }] },
    { name: "Subscriptions",      value: 10000, color: "#007AFF", emoji: "📱", tip: "Audit your subscriptions quarterly — cancel unused ones.",              transactions: [{ date: "Aug 10", description: "Netflix",            amount: 4000,  implication: "Recurring charges should be monitored and managed actively." }, { date: "Aug 20", description: "Spotify",            amount: 6000,  implication: "Music subscriptions are nice, but keep an eye on them." }] },
];

const monthlyTrend = [
    { month: "May", spending: 78000 },
    { month: "Jun", spending: 92000 },
    { month: "Jul", spending: 68000 },
    { month: "Aug", spending: 85000 },
];

const monthsOrder = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const DarkTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    return (
        <Box sx={{ background: B.dark, border: `1px solid ${alpha(B.light, .2)}`, borderRadius: 2, px: 1.5, py: 1, boxShadow: `0 8px 24px ${alpha(B.deep, .6)}` }}>
            <Typography sx={{ color: alpha('#fff', .4), fontSize: '.6rem', letterSpacing: '.08em', mb: .25 }}>{payload[0].payload.month || payload[0].name}</Typography>
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '.88rem', fontVariantNumeric: 'tabular-nums' }}>₦{Number(payload[0].value).toLocaleString()}</Typography>
        </Box>
    );
};

const SpendingAnalysis: React.FC = () => {
    const navigate  = useNavigate();
    const location  = useLocation();
    const theme     = useTheme();
    const isMobile  = useMediaQuery(theme.breakpoints.down("sm"));
    const isDark    = theme.palette.mode === "dark";

    const { bank, year, month } = location.state || { bank: "all", year: "2024", month: "August" };

    const [selectedYear,     setSelectedYear]     = useState(year);
    const [selectedMonth,    setSelectedMonth]    = useState(month);
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [displayedTotal,   setDisplayedTotal]   = useState(0);

    const totalSpending = useMemo(() => spendingData.reduce((sum, e) => sum + e.value, 0), []);

    useEffect(() => {
        let start = 0;
        const increment = totalSpending / (500 / 16);
        const timer = setInterval(() => {
            start += increment;
            if (start >= totalSpending) { setDisplayedTotal(totalSpending); clearInterval(timer); }
            else setDisplayedTotal(Math.round(start));
        }, 16);
        return () => clearInterval(timer);
    }, [totalSpending, selectedMonth, selectedYear]);

    const getChangePercent = (name: string, current: number) => {
        const prev = ({ Food: current * 1.1, "Credit Utilization": current * 0.8, Bills: current * 1.05, Subscriptions: current * 1.2 } as Record<string, number>)[name] || current;
        return prev === 0 ? null : ((current - prev) / prev) * 100;
    };

    const pieData = spendingData.map(d => ({ name: d.name, value: d.value, color: d.color }));
    const selectedCategoryData = spendingData.find(d => d.name === selectedCategory);

    const budgetScore = totalSpending < 70000 ? "Excellent" : totalSpending < 90000 ? "Good" : "Watch Out";
    const budgetColor = totalSpending < 70000 ? B.green : totalSpending < 90000 ? B.orange : B.red;
    const BudgetIcon  = totalSpending < 90000 ? CheckCircleOutline : WarningAmberOutlined;

    const surfaceBg  = isDark ? alpha(B.mid, .55) : '#fff';
    const surfaceBdr = `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}`;
    const surfaceShd = `0 4px 28px ${alpha(B.dark, isDark ? .25 : .06)}`;

    const filterSx = {
        color: '#fff',
        background: alpha('#fff', .1),
        borderRadius: 2,
        fontSize: '.78rem',
        fontFamily: "'DM Sans', system-ui, sans-serif",
        '.MuiOutlinedInput-notchedOutline': { borderColor: alpha('#fff', .2) },
        '.MuiSvgIcon-root': { color: alpha('#fff', .6) },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha('#fff', .4) },
    };

    return (
        <Box sx={{ minHeight: "100vh", background: isDark ? B.deep : B.lavender, pb: 10, fontFamily: "'DM Sans', system-ui, sans-serif" }}>

            {/* ══ HERO HEADER ════════════════════════════════════════ */}
            <Box sx={{
                background: `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${B.purple} 100%)`,
                mt: { xs: '-56px', sm: 0 },
                pt: { xs: 'calc(56px + 44px)', sm: '44px' },
                pb: 14, px: 3, position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -60, right: -40, width: 220, height: 220, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.red, .14)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', bottom: -20, left: -40, width: 180, height: 180, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.light, .08)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

                <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <IconButton onClick={() => navigate(-1)} sx={{ color: alpha('#fff', .7), background: alpha('#fff', .1), border: `1px solid ${alpha('#fff', .15)}`, width: 36, height: 36, '&:hover': { background: alpha('#fff', .2) } }}>
                            <ArrowBackIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                        <Box>
                            <Typography sx={{ color: alpha('#fff', .35), fontSize: '.62rem', letterSpacing: '.18em', textTransform: 'uppercase' }}>Analysis</Typography>
                            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem', letterSpacing: '-.025em', fontFamily: "'Instrument Serif', serif" }}>Spending Breakdown</Typography>
                        </Box>
                    </Box>
                    <IconButton sx={{ color: alpha('#fff', .55), background: alpha('#fff', .08), border: `1px solid ${alpha('#fff', .1)}`, width: 36, height: 36 }}>
                        <FileDownloadOutlined sx={{ fontSize: 17 }} />
                    </IconButton>
                </Box>

                <Box display="flex" gap={1.5}>
                    <Select size="small" value={selectedYear} onChange={e => setSelectedYear(e.target.value)} sx={{ ...filterSx, minWidth: 100 }}>
                        <MenuItem value="2024">2024</MenuItem>
                        <MenuItem value="2025">2025</MenuItem>
                    </Select>
                    <Select size="small" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} sx={{ ...filterSx, minWidth: 130 }}>
                        {monthsOrder.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                    </Select>
                </Box>
            </Box>

            <Container maxWidth="sm" sx={{ mt: -8, position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>

                {/* ── Hero summary ── */}
                <Box sx={{ mb: 2, borderRadius: 3, overflow: 'hidden', background: `linear-gradient(145deg, ${B.dark}, ${B.mid})`, boxShadow: `0 12px 40px ${alpha(B.deep, .45)}` }}>
                    <Box sx={{ p: 2.5 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Box>
                                <Typography sx={{ color: alpha('#fff', .35), fontSize: '.62rem', letterSpacing: '.18em', textTransform: 'uppercase', mb: .75 }}>
                                    {selectedMonth}, {selectedYear}
                                </Typography>
                                <Typography sx={{ color: '#fff', fontFamily: "'Instrument Serif', serif", fontSize: '2.6rem', fontWeight: 400, lineHeight: 1, letterSpacing: '-.02em', mb: .4 }}>
                                    ₦{displayedTotal.toLocaleString()}
                                </Typography>
                                <Typography sx={{ color: alpha('#fff', .35), fontSize: '.72rem', mb: 1.5 }}>Total Spending</Typography>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: .75, background: alpha(budgetColor, .12), border: `1px solid ${alpha(budgetColor, .25)}`, borderRadius: 8, px: 1.25, py: .5 }}>
                                    <BudgetIcon sx={{ fontSize: 12, color: budgetColor }} />
                                    <Typography sx={{ color: budgetColor, fontSize: '.63rem', fontWeight: 700, letterSpacing: '.04em' }}>
                                        BUDGET: {budgetScore.toUpperCase()}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ width: 80, height: 80, flexShrink: 0 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={pieData} cx="50%" cy="50%" innerRadius={24} outerRadius={38} dataKey="value" strokeWidth={0}>
                                            {pieData.map((d, i) => <Cell key={i} fill={d.color} />)}
                                        </Pie>
                                        <Tooltip content={<DarkTooltip />} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ px: 2.5, py: 1.5, background: alpha('#000', .2), display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                        {pieData.map(d => (
                            <Box key={d.name} display="flex" alignItems="center" gap={.6}>
                                <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: d.color }} />
                                <Typography sx={{ fontSize: '.63rem', color: alpha('#fff', .45), fontWeight: 600 }}>{d.name}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* ── Trend chart ── */}
                <Box sx={{ mb: 2, borderRadius: 3, background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd, p: 2.5 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Box>
                            <Typography sx={{ fontWeight: 700, fontSize: '.88rem', letterSpacing: '-.02em', color: isDark ? '#fff' : B.ink }}>4-Month Trend</Typography>
                            <Typography sx={{ fontSize: '.68rem', color: alpha(isDark ? '#fff' : B.ink, .35), mt: .2 }}>{selectedYear}</Typography>
                        </Box>
                        <CalendarTodayOutlined sx={{ fontSize: 15, color: alpha(isDark ? '#fff' : B.ink, .25) }} />
                    </Box>
                    <ResponsiveContainer width="100%" height={140}>
                        <BarChart data={monthlyTrend} barSize={28} margin={{ top: 4, right: 4, bottom: 0, left: -16 }}>
                            <CartesianGrid strokeDasharray="4 4" stroke={alpha(isDark ? '#fff' : '#000', .05)} vertical={false} />
                            <XAxis dataKey="month" tick={{ fontSize: 11, fill: alpha(isDark ? '#fff' : B.ink, .35) as string }} axisLine={false} tickLine={false} />
                            <YAxis tickFormatter={v => `₦${(v/1000).toFixed(0)}k`} tick={{ fontSize: 10, fill: alpha(isDark ? '#fff' : B.ink, .28) as string }} axisLine={false} tickLine={false} width={48} />
                            <Tooltip content={<DarkTooltip />} />
                            <defs>
                                <linearGradient id="spendGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={B.red} stopOpacity={.85} />
                                    <stop offset="100%" stopColor={B.red} stopOpacity={.2} />
                                </linearGradient>
                            </defs>
                            <Bar dataKey="spending" radius={[4,4,0,0]} fill="url(#spendGrad)" />
                        </BarChart>
                    </ResponsiveContainer>
                </Box>

                {/* ── Category cards ── */}
                <Typography sx={{ fontWeight: 700, fontSize: '.63rem', color: alpha(isDark ? '#fff' : B.ink, .3), letterSpacing: '.16em', textTransform: 'uppercase', mb: 1.5 }}>Categories</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 1.5, mb: 2 }}>
                    {spendingData.map(cat => {
                        const percent = ((cat.value / totalSpending) * 100).toFixed(1);
                        const change  = getChangePercent(cat.name, cat.value);
                        return (
                            <Box key={cat.name} onClick={() => setSelectedCategory(cat.name)} sx={{
                                borderRadius: 3, p: 2.25, cursor: 'pointer',
                                background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd,
                                transition: 'all .2s',
                                '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 12px 32px ${alpha(cat.color, .18)}`, borderColor: alpha(cat.color, .25) },
                            }}>
                                <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={1.5}>
                                    <Box display="flex" alignItems="center" gap={1.25}>
                                        <Box sx={{ width: 38, height: 38, borderRadius: 2.5, background: alpha(cat.color, .1), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.95rem', flexShrink: 0 }}>
                                            {cat.emoji}
                                        </Box>
                                        <Box>
                                            <Typography sx={{ fontWeight: 700, fontSize: '.82rem', letterSpacing: '-.015em', color: isDark ? '#fff' : B.ink }}>{cat.name}</Typography>
                                            <Typography sx={{ color: cat.color, fontWeight: 700, fontSize: '.95rem', fontFamily: "'Instrument Serif', serif" }}>
                                                ₦{cat.value.toLocaleString()}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {change !== null && (
                                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: .2, background: alpha(change > 0 ? B.red : B.green, .1), borderRadius: 8, px: .9, py: .35, flexShrink: 0 }}>
                                            {change > 0 ? <ArrowDropUpIcon sx={{ color: B.red, fontSize: 14 }} /> : <ArrowDropDownIcon sx={{ color: B.green, fontSize: 14 }} />}
                                            <Typography sx={{ color: change > 0 ? B.red : B.green, fontSize: '.62rem', fontWeight: 700 }}>{Math.abs(change).toFixed(1)}%</Typography>
                                        </Box>
                                    )}
                                </Box>
                                <Box sx={{ height: 4, borderRadius: 4, background: alpha(isDark ? '#fff' : B.dark, .07), overflow: 'hidden', mb: .75 }}>
                                    <Box sx={{ width: `${percent}%`, height: '100%', borderRadius: 4, background: `linear-gradient(90deg, ${B.purple}, ${cat.color})`, transition: 'width .8s cubic-bezier(.16,1,.3,1)' }} />
                                </Box>
                                <Typography sx={{ fontSize: '.63rem', color: alpha(isDark ? '#fff' : B.ink, .3), fontWeight: 600 }}>{percent}% of total</Typography>
                            </Box>
                        );
                    })}
                </Box>

                {/* ── Smart tips ── */}
                <Box sx={{ borderRadius: 3, background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd, overflow: 'hidden' }}>
                    <Box sx={{ px: 2.5, pt: 2.25, pb: 2, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}` }}>
                        <Box sx={{ width: 32, height: 32, borderRadius: 2, background: alpha(B.orange, .1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <InfoOutlined sx={{ color: B.orange, fontSize: 16 }} />
                        </Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '.88rem', letterSpacing: '-.02em', color: isDark ? '#fff' : B.ink }}>Smart Spending Tips</Typography>
                    </Box>
                    <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
                        {spendingData.map(cat => (
                            <Box key={cat.name} display="flex" gap={1.5} alignItems="flex-start">
                                <Box sx={{ width: 28, height: 28, borderRadius: 2, background: alpha(cat.color, .1), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.8rem', flexShrink: 0 }}>
                                    {cat.emoji}
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: '.78rem', fontWeight: 600, color: isDark ? '#fff' : B.ink, mb: .2 }}>{cat.name}</Typography>
                                    <Typography sx={{ fontSize: '.74rem', color: alpha(isDark ? '#fff' : B.ink, .45), lineHeight: 1.6 }}>{cat.tip}</Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Container>

            {/* ── Dialog ── */}
            <Dialog open={!!selectedCategory} onClose={() => setSelectedCategory(null)} fullWidth maxWidth="sm"
                    PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', background: isDark ? B.dark : '#fff', backgroundImage: 'none' } }}>
                {selectedCategoryData && (
                    <>
                        <Box sx={{ background: `linear-gradient(145deg, ${B.deep}, ${B.mid})`, px: 2.5, pt: 2.5, pb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box display="flex" alignItems="center" gap={1.5}>
                                <Box sx={{ width: 40, height: 40, borderRadius: 2.5, background: alpha(selectedCategoryData.color, .15), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem' }}>
                                    {selectedCategoryData.emoji}
                                </Box>
                                <Box>
                                    <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1rem', letterSpacing: '-.02em' }}>{selectedCategoryData.name}</Typography>
                                    <Typography sx={{ color: alpha('#fff', .4), fontSize: '.7rem' }}>₦{selectedCategoryData.value.toLocaleString()} this month</Typography>
                                </Box>
                            </Box>
                            <IconButton size="small" onClick={() => setSelectedCategory(null)} sx={{ color: alpha('#fff', .4), '&:hover': { color: '#fff' } }}>
                                <CloseOutlined sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Box>
                        <Box sx={{ px: 2.5, py: 1.75, background: alpha(selectedCategoryData.color, .05), borderBottom: `1px solid ${alpha(selectedCategoryData.color, .1)}` }}>
                            <Typography sx={{ fontSize: '.76rem', color: alpha(isDark ? '#fff' : B.ink, .5), lineHeight: 1.6, fontStyle: 'italic' }}>{selectedCategoryData.tip}</Typography>
                        </Box>
                        {selectedCategoryData.transactions.map((tx: any, idx: number, arr: any[]) => (
                            <Box key={idx}>
                                <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                    <Box sx={{ width: 36, height: 36, borderRadius: 2.5, background: alpha(selectedCategoryData.color, .1), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <TrendingDownOutlined sx={{ color: selectedCategoryData.color, fontSize: 17 }} />
                                    </Box>
                                    <Box flex={1}>
                                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                                            <Typography sx={{ fontWeight: 600, fontSize: '.85rem', color: isDark ? '#fff' : B.ink }}>{tx.description}</Typography>
                                            <Typography sx={{ fontWeight: 700, color: B.red, fontSize: '.88rem', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>−₦{tx.amount.toLocaleString()}</Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: '.68rem', color: alpha(isDark ? '#fff' : B.ink, .35), mt: .25 }}>{tx.date}</Typography>
                                        <Box sx={{ mt: 1, p: 1.25, background: alpha(selectedCategoryData.color, .06), borderRadius: 2, borderLeft: `2.5px solid ${selectedCategoryData.color}` }}>
                                            <Typography sx={{ fontSize: '.74rem', color: alpha(isDark ? '#fff' : B.ink, .5), lineHeight: 1.5 }}>{tx.implication}</Typography>
                                        </Box>
                                    </Box>
                                </Box>
                                {idx < arr.length - 1 && <Divider sx={{ mx: 2.5, borderColor: alpha(isDark ? '#fff' : B.dark, .07) }} />}
                            </Box>
                        ))}
                        <Box sx={{ px: 2.5, pb: 2.5, pt: 1.5 }}>
                            <Button fullWidth onClick={() => setSelectedCategory(null)} sx={{
                                background: alpha(B.purple, .08), color: B.purple, borderRadius: 2.5,
                                textTransform: 'none', fontWeight: 700, fontSize: '.8rem', py: 1.2,
                                border: `1px solid ${alpha(B.purple, .2)}`,
                                '&:hover': { background: alpha(B.purple, .15) },
                            }}>
                                Close
                            </Button>
                        </Box>
                    </>
                )}
            </Dialog>
        </Box>
    );
};

export default SpendingAnalysis;